<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CustomDialog from '@/components/CustomDialog.vue'
import { useApi } from '@/composables/useApi'
import { getAgreementInfo, agreeAgreement } from '@/api/user'
import { useAppParams } from '@/stores/app'
import { postMessageToRN, openProtocolToRN } from '@/bridge'

defineOptions({ name: 'PrivacyDialog' })

const { t } = useI18n()

// ---- State ----
const appParams = useAppParams()
const visible = ref(false)
const content = ref('') // 接口返回的协议变更内容
const status = ref(0) // 更新状态：1-用户协议 2-隐私协议 3-两者

// silent: 查询/同意失败不弹默认 Toast，避免打断启动流程（查询失败直接不弹，由业务侧下次进入重试）
const { executeImmediate: runGetInfo } = useApi(getAgreementInfo, null, { immediate: false, silent: true })
const { executeImmediate: runAgree } = useApi(agreeAgreement, null, { immediate: false })

// ---- Derived values ----
// 是否展示对应协议链接（status：1-用户协议 2-隐私协议 3-两者）
const showUserAgreement = computed(() => status.value === 1 || status.value === 3)
const showPrivacyPolicy = computed(() => status.value === 2 || status.value === 3)

// ---- Actions ----
// 挂载后查询协议：有登录态且有更新（status !== 0）时弹窗，展示接口返回内容
async function check() {
  if (!appParams.token) return
  try {
    const data = await runGetInfo()
    if (data && data.status !== 0) {
      content.value = data.content
      status.value = data.status
      visible.value = true
    }
  } catch {
    // 查询失败静默处理，不阻塞启动
  }
}

// 同意：调接口成功后关闭弹窗
async function handleConfirm() {
  try {
    await runAgree()
    visible.value = false
  } catch {
    // 同意失败保留弹窗，错误 Toast 由 useApi 默认弹出
  }
}

// 不同意并退出：关闭弹窗并退出到原生
function handleDecline() {
  postMessageToRN({ type: 'exitApp' })
}

onMounted(check)
</script>

<template>
  <!-- z-index 9000：稳定覆盖其他 vant 弹窗（默认 2000 起），又低于 NewVersionModal(9999) -->
  <CustomDialog :visible="visible" title="更新通知" :z-index="9000" cancel-text="不同意并退出" confirm-text="同意"
    @cancel="handleDecline" @confirm="handleConfirm">
    <!-- 固定开头 + 接口返回的更新内容 + 固定结尾；text-left 覆盖父级 slot 的 text-center -->
    <div class="text-sm leading-[1.4em] text-ink/50 text-left space-y-1">
      <p>
        我们更新了<span v-if="showUserAgreement" class="text-[#398CFF]"
          @click="openProtocolToRN(t('userAgreement'), 'user-agreement')">《用户协议》</span><span
          v-if="showUserAgreement && showPrivacyPolicy">和</span><span v-if="showPrivacyPolicy" class="text-[#398CFF]"
          @click="openProtocolToRN(t('privacyPolicy'), 'privacy')">《隐私政策》</span>。
      </p>
      <p>本次更新主要包括：</p>
      <!-- 更新内容由接口返回，纯文本保留换行；封顶 40vh，超出仅此段滚动，固定文案常驻可见 -->
      <p class="whitespace-pre-line max-h-[40vh] overflow-y-auto">{{ content }}</p>
      <p>请您认真阅读并确认最新版本协议。确认并同意后，您将可以继续使用我们的产品和服务。</p>
    </div>
  </CustomDialog>
</template>
